import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
} from 'react-native';
import DropDownPicker from 'react-native-dropdown-picker';
import { DLODropdownOption } from '../../types/dlo.types';
import { DayItem } from '../DLOFilterByDayModal';
import { EmployeeRowData } from './DLOCreateScheduleEmployeeRow';
import DLOTooltip from '../DLOTooltip';

export interface DLOCreateScheduleActualSplitRoleRowProps {
  data: EmployeeRowData;
  parentData?: EmployeeRowData; // Parent row data to check shift selection
  onUpdate: (id: string, updatedData: Partial<EmployeeRowData>) => void;
  onDelete?: (id: string) => void;
  roleOptions: DLODropdownOption[];
  shiftOptions?: DLODropdownOption[];
  selectedDays: string[];
  dayFilter?: DayItem[];
  // Global dropdown management props
  isRoleDropdownOpen?: boolean;
  isShiftDropdownOpen?: boolean;
  onDropdownOpen?: (rowId: string, type: 'role' | 'shift', isOpen: boolean) => void;
  // Loading states
  isRoleLoading?: boolean;
  isShiftLoading?: boolean;
  // Schedule employee rows for calculating schedule variance
  scheduleEmployeeRows?: any[];
  // Checkbox control
  isLastChild?: boolean; // Whether this is the last child split role for its parent
  // Input control
  inputsEnabled?: boolean; // Controls whether inputs are editable based on week cutoff
  disabledDays?: string[]; // <-- Add to props
}

const DLOCreateScheduleActualSplitRoleRow: React.FC<DLOCreateScheduleActualSplitRoleRowProps> = ({
  data,
  parentData,
  onUpdate,
  onDelete,
  roleOptions,
  shiftOptions = [],
  selectedDays,
  dayFilter = [],
  isRoleDropdownOpen = false,
  isShiftDropdownOpen = false,
  onDropdownOpen,
  isRoleLoading = false,
  isShiftLoading = false,
  scheduleEmployeeRows = [],
  isLastChild = false,
  inputsEnabled = true,
  disabledDays = [], // <-- Add to props
}) => {
  // Determine initial lock state: unlock if parent has no shift, otherwise use data.isLocked or default to true
  const getInitialLockState = () => {
    if (parentData && (!parentData.shift || !parentData.shift.value)) {
      return false; // Unlock if parent has no shift
    }
    return data.isLocked ?? true; // Use existing lock state or default to locked
  };

  const [isShiftLocked, setIsShiftLocked] = useState(getInitialLockState());
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [shiftDropdownOpen, setShiftDropdownOpen] = useState(false);
  const [roleValue, setRoleValue] = useState(data.role?.value || null);
  const [shiftValue, setShiftValue] = useState(data.shift?.value || null);

  // Handle checkbox change - restores normal selection behavior
  const handleCheckboxChange = () => {
    onUpdate(data.id, { isSelected: !data.isSelected });
  };

  // All child row checkboxes are now enabled (removed isLastChild restriction)
  const shouldDisableCheckbox = false;

  // Validation logic for sequential dropdown enabling in actual split role rows
  const isShiftDropdownEnabled = (): boolean => {
    // Always enable if data is prepopulated (existing split role row)
    if (data.role && data.shift) {
      return inputsEnabled && !isShiftLocked;
    }
    // For new split role rows, only enable shift if role is selected
    return inputsEnabled && !isShiftLocked && !!data.role;
  };

  // Handle role selection
  const handleRoleSelect = (value: string | null) => {
    if (!inputsEnabled) return;
    setRoleValue(value);
    const selectedRole = roleOptions.find(option => option.value === value) || null;
    
    // When role changes, clear shift if this is a new split role row
    const updates: Partial<EmployeeRowData> = { role: selectedRole };
    
    // If this was a new split role row without prepopulated data, clear shift
    if (!data.role && !data.shift) {
      updates.shift = null;
      setShiftValue(null);
    }
    
    onUpdate(data.id, updates);
  };

  // Handle shift selection
  const handleShiftSelect = (value: string | null) => {
    if (!inputsEnabled) return;
    setShiftValue(value);
    const selectedShift = shiftOptions.find(option => option.value === value) || null;
    onUpdate(data.id, { shift: selectedShift });
  };

  // Handle hour field changes
  const handleHourChange = (day: string, value: string) => {
    if (!inputsEnabled) return;
    // Validate input - only allow numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Calculate new total
    const updatedHours = {
      sunday: day === 'sunday' ? numericValue : data.sunday,
      monday: day === 'monday' ? numericValue : data.monday,
      tuesday: day === 'tuesday' ? numericValue : data.tuesday,
      wednesday: day === 'wednesday' ? numericValue : data.wednesday,
      thursday: day === 'thursday' ? numericValue : data.thursday,
      friday: day === 'friday' ? numericValue : data.friday,
      saturday: day === 'saturday' ? numericValue : data.saturday,
    };
    
    const newTotal = Object.values(updatedHours).reduce((sum, hours) => {
      return sum + (parseFloat(hours) || 0);
    }, 0);
    
    onUpdate(data.id, { 
      [day]: numericValue,
      total: newTotal.toString()
    });
  };

  // Handle lock/unlock toggle
  const handleLockToggle = () => {
    const newLockState = !isShiftLocked;
    setIsShiftLocked(newLockState);
    onUpdate(data.id, { isLocked: newLockState });
  };

  // Handle delete
  const handleDelete = () => {
    if (onDelete) {
      onDelete(data.id);
    }
  };

  // Calculate schedule variance for this split role
  const calculateScheduleVariance = (): string => {
    // Schedule Variance = (Total Scheduled Hours - Total Actual Hours)
    
    try {
      // Find corresponding schedule row for this split role
      // Actual row IDs have format "actual_XXX", need to find schedule row with "schedule_XXX" or just "XXX"
      let correspondingScheduleRow: any = null;
      
      if (data.id.startsWith('actual_')) {
        const scheduleId = data.id.replace('actual_', 'schedule_');
        correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === scheduleId);
        
        // If not found with "schedule_" prefix, try without any prefix
        if (!correspondingScheduleRow) {
          const baseId = data.id.replace('actual_', '');
          correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === baseId);
        }
      } else {
        // If actual ID doesn't have "actual_" prefix, try direct match or with "schedule_" prefix
        correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === data.id);
        if (!correspondingScheduleRow) {
          correspondingScheduleRow = scheduleEmployeeRows.find(row => row.id === `schedule_${data.id}`);
        }
      }
      
      if (!correspondingScheduleRow) {
        return "0.00";
      }
      
      // For split roles, calculate only this split role's hours (not parent + children)
      const scheduleHours = [
        correspondingScheduleRow.sunday || '0',
        correspondingScheduleRow.monday || '0',
        correspondingScheduleRow.tuesday || '0',
        correspondingScheduleRow.wednesday || '0',
        correspondingScheduleRow.thursday || '0',
        correspondingScheduleRow.friday || '0',
        correspondingScheduleRow.saturday || '0'
      ];
      
      const totalScheduledHours = scheduleHours.reduce((sum, value) => {
        const numericValue = parseFloat(value) || 0;
        return sum + numericValue;
      }, 0);
      
      // Calculate total actual hours for this split role
      const actualHours = [
        data.sunday || '0',
        data.monday || '0',
        data.tuesday || '0',
        data.wednesday || '0',
        data.thursday || '0',
        data.friday || '0',
        data.saturday || '0'
      ];
      
      const totalActualHours = actualHours.reduce((sum, value) => {
        const numericValue = parseFloat(value) || 0;
        return sum + numericValue;
      }, 0);
      
      // Calculate variance: (Total Scheduled Hours - Total Actual Hours)
      // Always show as positive value using Math.abs()
      const variance = Math.abs(totalScheduledHours - totalActualHours);
      
      return variance.toFixed(2);
      
    } catch (error) {
      console.error('Error calculating schedule variance for split role:', error);
      return "0.00";
    }
  };

  // Get shift display value - non-editable showing parent's shift
  const getShiftDisplayValue = () => {
    if (data.shift) {
      return data.shift.label;
    }
    return '';
  };

  // Check if day is disabled based on dayFilter, inputsEnabled, and disabledDays prop
  const isDayDisabled = (dayName: string): boolean => {
    const dayKey = dayName.toLowerCase();
    if (Array.isArray(disabledDays) && disabledDays.includes(dayKey)) {
      return true;
    }
    const dayItem = dayFilter.find(d => d.name.toLowerCase() === dayKey);
    if (dayItem && !dayItem.enabled) return true;
    return !inputsEnabled;
  };

  // Check if day is selected
  const isDaySelected = (dayKey: string): boolean => {
    return selectedDays.includes(dayKey);
  };

  // Get cell style based on day state
  const getDayCellStyle = (dayName: string, dayKey: string) => {
    const isDisabled = isDayDisabled(dayName);
    const isSelected = isDaySelected(dayKey);
    
    if (isDisabled) {
      return [styles.dayCell, styles.disabledDayCell];
    }
    if (isSelected) {
      return [styles.dayCell, styles.selectedDayCell];
    }
    return styles.dayCell;
  };

  // Lock Icon SVG
  const LockIcon = () => (
    <svg width="13" height="15" viewBox="0 0 13 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.625 5H8.9375V3.57143C8.9375 2.97619 8.70052 2.47024 8.22656 2.05357C7.7526 1.63691 7.17708 1.42857 6.5 1.42857C5.82292 1.42857 5.2474 1.63691 4.77344 2.05357C4.29948 2.47024 4.0625 2.97619 4.0625 3.57143H2.4375C2.4375 2.58333 2.83373 1.74119 3.62619 1.045C4.41865 0.34881 5.37658 0.000476678 6.5 4.87567e-07C7.62342 -0.000475703 8.58162 0.347858 9.37462 1.045C10.1676 1.74214 10.5636 2.58429 10.5625 3.57143V5H11.375C11.8219 5 12.2046 5.14 12.5231 5.42C12.8416 5.7 13.0005 6.03619 13 6.42857V13.5714C13 13.9643 12.841 14.3007 12.5231 14.5807C12.2051 14.8607 11.8224 15.0005 11.375 15H1.625C1.17812 15 0.795708 14.8602 0.47775 14.5807C0.159792 14.3012 0.000541667 13.9648 0 13.5714V6.42857C0 6.03571 0.15925 5.69952 0.47775 5.42C0.79625 5.14048 1.17867 5.00048 1.625 5ZM1.625 13.5714H11.375V6.42857H1.625V13.5714ZM6.5 11.4286C6.94687 11.4286 7.32956 11.2888 7.64806 11.0093C7.96656 10.7298 8.12554 10.3933 8.125 10C8.12446 9.60667 7.96548 9.27048 7.64806 8.99143C7.33065 8.71238 6.94796 8.57238 6.5 8.57143C6.05204 8.57048 5.66962 8.71048 5.35275 8.99143C5.03587 9.27238 4.87662 9.60857 4.875 10C4.87337 10.3914 5.03262 10.7279 5.35275 11.0093C5.67287 11.2907 6.05529 11.4305 6.5 11.4286Z" fill="#3A3A3A"/>
    </svg>
  );

  // Unlock Icon SVG
  const UnlockIcon = () => (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.58832 13.5714H11.1182V6.42857H1.58832V13.5714ZM6.35327 11.4286C6.79005 11.4286 7.1641 11.2888 7.47541 11.0093C7.78672 10.7298 7.94211 10.3933 7.94158 10C7.94105 9.60667 7.78566 9.27048 7.47541 8.99143C7.16516 8.71238 6.79111 8.57238 6.35327 8.57143C5.91542 8.57048 5.54164 8.71048 5.23191 8.99143C4.92219 9.27238 4.76654 9.60857 4.76495 10C4.76336 10.3914 4.91902 10.7279 5.23191 11.0093C5.54481 11.2907 5.9186 11.4305 6.35327 11.4286ZM1.58832 15C1.15153 15 0.777745 14.8602 0.466965 14.5807C0.156184 14.3012 0.000529439 13.9648 0 13.5714V6.42857C0 6.03571 0.155655 5.69952 0.466965 5.42C0.778275 5.14048 1.15206 5.00048 1.58832 5H7.14742V3.57143C7.14742 2.58333 7.53471 1.74119 8.30928 1.045C9.08385 0.34881 10.0202 0.00047619 11.1182 0C12.058 0 12.8786 0.255952 13.5801 0.767857C14.2816 1.27976 14.7449 1.92262 14.9699 2.69643C15.0361 2.89881 14.9932 3.09524 14.8412 3.28571C14.6893 3.47619 14.5072 3.57143 14.2948 3.57143C14.0698 3.57143 13.8845 3.52976 13.7389 3.44643C13.5933 3.36309 13.4875 3.22619 13.4213 3.03571C13.2757 2.58333 12.9945 2.20238 12.5779 1.89286C12.1612 1.58333 11.6747 1.42857 11.1182 1.42857C10.4564 1.42857 9.89389 1.6369 9.43063 2.05357C8.96737 2.47024 8.73574 2.97619 8.73574 3.57143V5H11.1182C11.555 5 11.9291 5.14 12.2404 5.42C12.5517 5.7 12.7071 6.03619 12.7065 6.42857V13.5714C12.7065 13.9643 12.5511 14.3007 12.2404 14.5807C11.9296 14.8607 11.5555 15.0005 11.1182 15H1.58832Z" fill="#3A3A3A"/>
    </svg>
  );

  // Delete Icon SVG
  const DeleteIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 6.75V14.25H6V6.75H12ZM10.875 2.25H7.125L6.375 3H3.75V4.5H14.25V3H11.625L10.875 2.25ZM13.5 5.25H4.5V14.25C4.5 15.075 5.175 15.75 6 15.75H12C12.825 15.75 13.5 15.075 13.5 14.25V5.25Z" fill="#3A3A3A"/>
    </svg>
  );

  return (
    <View style={styles.container}>
      {/* Checkbox Column */}
      <View style={styles.checkboxCell}>
        <TouchableOpacity
          style={[
            styles.checkboxContainer,
            shouldDisableCheckbox && styles.disabledCheckboxContainer
          ]}
          onPress={handleCheckboxChange}
          activeOpacity={shouldDisableCheckbox ? 1 : 0.8}
          disabled={shouldDisableCheckbox}
        >
          <View style={[
            styles.checkbox,
            data.isSelected && styles.checkboxSelected,
            shouldDisableCheckbox && styles.disabledCheckbox
          ]}>
            {data.isSelected && <Text style={styles.checkboxCheck}>✓</Text>}
          </View>
        </TouchableOpacity>
      </View>

      {/* Actions Column with Lock and Delete Icons */}
      <View style={styles.actionsCell}>
        <View style={styles.actionsContainer}>
          <TouchableOpacity onPress={handleLockToggle} style={styles.iconButton}>
            {isShiftLocked ? <LockIcon /> : <UnlockIcon />}
          </TouchableOpacity>
          <TouchableOpacity onPress={handleDelete} style={styles.iconButton}>
            <DeleteIcon />
          </TouchableOpacity>
        </View>
      </View>

      {/* Role Column */}
      <View style={styles.roleCell}>
        <DropDownPicker
          open={roleDropdownOpen}
          value={roleValue}
          items={roleOptions.map(option => ({
            label: option.label,
            value: option.value
          }))}
          setOpen={setRoleDropdownOpen}
          setValue={setRoleValue}
          onSelectItem={(item) => handleRoleSelect(item.value || null)}
          placeholder="Select Role"
          disabled={isRoleLoading || !inputsEnabled}
          style={[styles.dropdown, !inputsEnabled && styles.dropdownStyleDisabled]}
          dropDownContainerStyle={styles.dropdownContainer}
          textStyle={styles.dropdownText}
          placeholderStyle={styles.dropdownPlaceholder}
          zIndex={1000}
          zIndexInverse={3000}
          dropDownDirection='TOP'
        />
      </View>

      {/* Shift Column - Editable when unlocked, read-only when locked */}
      <View style={styles.shiftCell}>
        {isShiftLocked || !inputsEnabled ? (
          <View style={styles.shiftDisplayContainer}>
            <Text style={styles.shiftDisplayText}>{getShiftDisplayValue()}</Text>
          </View>
        ) : (
          <DropDownPicker
            open={shiftDropdownOpen}
            value={shiftValue}
            items={shiftOptions.map(option => ({
              label: option.label,
              value: option.value
            }))}
            setOpen={setShiftDropdownOpen}
            setValue={setShiftValue}
            onSelectItem={(item) => handleShiftSelect(item.value || null)}
            placeholder="Select Shift"
            disabled={isShiftLoading || !isShiftDropdownEnabled()}
            style={[styles.dropdown, !isShiftDropdownEnabled() && styles.dropdownStyleDisabled]}
            dropDownContainerStyle={styles.dropdownContainer}
            textStyle={styles.dropdownText}
            placeholderStyle={styles.dropdownPlaceholder}
            zIndex={999}
            zIndexInverse={3001}
            dropDownDirection='TOP'
          />
        )}
      </View>

      {/* Day Columns - Editable Input Fields */}
      <View style={getDayCellStyle('Sunday', 'sun')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.sunday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Sunday') && styles.disabledHourInput]}
            value={data.sunday}
            onChangeText={!isDayDisabled('Sunday') ? (value) => handleHourChange('sunday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Sunday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Monday', 'mon')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.monday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Monday') && styles.disabledHourInput]}
            value={data.monday}
            onChangeText={!isDayDisabled('Monday') ? (value) => handleHourChange('monday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Monday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Tuesday', 'tue')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.tuesday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Tuesday') && styles.disabledHourInput]}
            value={data.tuesday}
            onChangeText={!isDayDisabled('Tuesday') ? (value) => handleHourChange('tuesday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Tuesday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Wednesday', 'wed')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.wednesday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Wednesday') && styles.disabledHourInput]}
            value={data.wednesday}
            onChangeText={!isDayDisabled('Wednesday') ? (value) => handleHourChange('wednesday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Wednesday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Thursday', 'thu')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.thursday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Thursday') && styles.disabledHourInput]}
            value={data.thursday}
            onChangeText={!isDayDisabled('Thursday') ? (value) => handleHourChange('thursday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Thursday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Friday', 'fri')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.friday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Friday') && styles.disabledHourInput]}
            value={data.friday}
            onChangeText={!isDayDisabled('Friday') ? (value) => handleHourChange('friday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Friday')}
          />
        </DLOTooltip>
      </View>
      <View style={getDayCellStyle('Saturday', 'sat')}>
        <DLOTooltip
          content={(() => {
            const scheduleRow = scheduleEmployeeRows?.find(row => row.id === data.id);
            return scheduleRow ? scheduleRow.saturday : '';
          })()}
          position="right"
          trigger="hover"
          disabled={!inputsEnabled}
          horizontalOffset={92}
        >
          <TextInput
            style={[styles.hourInput, isDayDisabled('Saturday') && styles.disabledHourInput]}
            value={data.saturday}
            onChangeText={!isDayDisabled('Saturday') ? (value) => handleHourChange('saturday', value) : undefined}
            keyboardType="numeric"
            editable={!isDayDisabled('Saturday')}
          />
        </DLOTooltip>
      </View>

      {/* Total Column */}
      <View style={styles.totalCell}>
        <TextInput
          style={[
            styles.totalInput,
            !inputsEnabled && styles.disabledTotalInput
          ]}
          value={data.total || '0'}
          editable={false}
          textAlign="center"
        />
      </View>

      {/* Schedule Variance Column */}
      <View style={styles.scheduleVarianceCell}>
        <TextInput
          style={styles.scheduleVarianceInput}
          value={calculateScheduleVariance()}
          editable={false}
          textAlign="center"
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: 1416,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9F9F9',
    borderBottomWidth: 1,
    borderBottomColor: '#DFDFDF',
  },
  checkboxCell: {
    width: 45,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    borderLeftWidth: 1,
    borderLeftColor: '#DFDFDF',
  },
  checkboxContainer: {
    padding: 4,
  },
  disabledCheckboxContainer: {
    opacity: 0.5,
  },
  checkbox: {
    width: 12,
    height: 12,
    borderWidth: 1,
    borderColor: '#DFDFDF',
    borderRadius: 3,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledCheckbox: {
    backgroundColor: '#F5F5F5',
    borderColor: '#C0C0C0',
  },
  checkboxSelected: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },
  checkboxCheck: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: 'bold',
  },
  actionsCell: {
    width: 184,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  iconButton: {
    padding: 4,
    borderRadius: 4,
  },
  roleCell: {
    width: 166,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  shiftCell: {
    width: 166,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 8,
  },
  shiftDisplayContainer: {
    width: '100%',
    height: 28,
    backgroundColor: '#F8F9FA',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#DFDFDF',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  shiftDisplayText: {
    fontSize: 12,
    color: '#666',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dayCell: {
    width: 93,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
  },
  selectedDayCell: {
    backgroundColor: '#E3F2FD',
  },
  disabledDayCell: {
    backgroundColor: '#F5F5F5',
  },
  dayText: {
    fontSize: 14,
    color: '#333',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  hourInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#C0C0C0',
    backgroundColor: '#FBFCFE',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
  },
  disabledHourInput: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#999999',
    opacity: 0.8,
    width: 76, // Ensure width is maintained when disabled
  },
  totalCell: {
    width: 101,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 10,
  },
  totalInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
  },
  disabledTotalInput: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    color: '#999999',
    opacity: 0.8,
    width: 76, // Ensure width is maintained when disabled
  },
  scheduleVarianceCell: {
    width: 103,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#DFDFDF',
    paddingHorizontal: 10,
  },
  scheduleVarianceInput: {
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    backgroundColor: '#F5F5F5',
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    lineHeight: 18,
    paddingHorizontal: 8,
    minHeight: 24,
    textAlign: 'center',
    width: 76,
  },
  dropdown: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    minHeight: 24,
    height: 24,
  },
  dropdownStyleDisabled: {
    backgroundColor: '#F5F5F5',
    borderColor: '#D3D3D3',
    opacity: 0.7,
  },
  dropdownContainer: {
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#C0C0C0',
    borderRadius: 4,
    marginTop: 2,
  },
  dropdownText: {
    fontSize: 12,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  dropdownPlaceholder: {
    fontSize: 12,
    color: '#666',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOCreateScheduleActualSplitRoleRow;